
import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "../../../utils/api";
import { FaChevronLeft, FaChevronRight, FaSearch } from "react-icons/fa";
import "../casino/CasinoPage.css"; // Reuse existing casino styles
import "./ProviderSinglePage.css";
import LoginModal from "../../../userAuth/LoginModal";
import GameCard from "../components/GameCard"; // Import shared GameCard
import { useSelector } from "react-redux";
import { useToast } from "../../../../context/ToastContext";

// Reuse icons logic from CasinoPage if possible, or duplicate for now to allow independence
import { FaHome, FaGamepad, FaDice, FaTrophy } from "react-icons/fa";
import { MdLiveTv, MdCasino, MdSportsSoccer } from "react-icons/md";
import { GiCardRandom, GiPokerHand, GiRollingDices } from "react-icons/gi";

const getIconForType = (typeId) => {
    const id = typeId.toLowerCase();
    if (id === 'all') return <FaHome />;
    if (id.includes('live')) return <MdLiveTv />;
    if (id.includes('slot')) return <MdCasino />;
    if (id.includes('card') || id.includes('poker')) return <GiPokerHand />;
    if (id.includes('table')) return <GiCardRandom />;
    if (id.includes('virtual') || id.includes('sport')) return <MdSportsSoccer />;
    if (id.includes('sic') || id.includes('dice')) return <FaDice />;
    if (id.includes('crash')) return <FaGamepad />;
    if (id.includes('lottery')) return <GiRollingDices />;
    return <FaTrophy />;
};

const ProviderSinglePage = () => {
    const { slug } = useParams();
    const [provider, setProvider] = useState(null);
    const [games, setGames] = useState([]);
    const [loading, setLoading] = useState(true);
    const [availableTypes, setAvailableTypes] = useState([]);
    const [activeType, setActiveType] = useState('all');
    const [searchQuery, setSearchQuery] = useState("");

    // Auth & Navigation
    const user = useSelector((state) => state.user);
    const [showLogin, setShowLogin] = useState(false);
    const { showToast } = useToast();
    const navigate = useNavigate();

    const navRef = useRef(null);

    useEffect(() => {
        const fetchProviderAndGames = async () => {
            try {
                setLoading(true);
                const providerRes = await api.get(`/providers/${slug}`);
                if (!providerRes.data.success) {
                    throw new Error("Provider not found");
                }
                const providerData = providerRes.data.data;
                setProvider(providerData);

                const gamesRes = await api.get(`/games?providerName=${providerData.name}&limit=1000`);
                if (gamesRes.data.success) {
                    const fetchedGames = gamesRes.data.data;
                    setGames(fetchedGames);

                    // Calculate counts based on fetched games
                    const typeCounts = {};
                    fetchedGames.forEach(g => {
                        const type = g.gameType || 'uncategorized';
                        typeCounts[type] = (typeCounts[type] || 0) + 1;
                    });

                    // Parse availableGameTypes from response OR derive from fetched games
                    let typesList = gamesRes.data.availableGameTypes || [];

                    // If API doesn't return types, derived from games
                    if (typesList.length === 0) {
                        typesList = Object.keys(typeCounts);
                    } else if (typeCounts['uncategorized'] && !typesList.includes('uncategorized')) {
                        // Ensure uncategorized is added if it exists
                        typesList.push('uncategorized');
                    }

                    const typesMapped = typesList
                        .map(t => ({
                            id: t,
                            label: t === 'uncategorized' ? 'UNCATEGORIZED' : t.replace(/-/g, ' ').toUpperCase(),
                            count: typeCounts[t] || 0
                        }))
                        .filter(t => t.count > 0); // Only show types that have games

                    // Sort so UNCATEGORIZED is first among the specific types
                    const uncategorized = typesMapped.filter(t => t.id === 'uncategorized');
                    const others = typesMapped.filter(t => t.id !== 'uncategorized');

                    setAvailableTypes([
                        { id: 'all', label: 'ALL GENRES', count: fetchedGames.length },
                        ...uncategorized,
                        ...others
                    ]);
                }

            } catch (error) {
                console.error("Error loading provider page:", error);
            } finally {
                setLoading(false);
            }
        };

        if (slug) {
            fetchProviderAndGames();
        }
    }, [slug]);

    const scroll = (ref, scrollOffset) => {
        if (ref.current) {
            ref.current.scrollLeft += scrollOffset;
        }
    };

    const getImageUrl = (img) => {
        if (!img) return null;
        return img;
    };

    // Filter Logic
    const filteredGames = games.filter(game => {
        let matchesType = false;
        if (activeType === 'all') {
            matchesType = true;
        } else if (activeType === 'uncategorized') {
            matchesType = !game.gameType;
        } else {
            matchesType = game.gameType === activeType;
        }

        const matchesSearch = game.name.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesType && matchesSearch;
    });

    // handleGameClick moved to GameCard component

    if (loading) return <div className="casino-loading">Loading Provider Games...</div>;
    if (!provider) return <div className="casino-loading">Provider Not Found</div>;

    return (
        <div className="casino-page provider-single-page">
            {/* Banner / Header */}
            <div className="casino-banner-section" style={{ height: '250px' }}>
                <div className="provider-header-content">
                    {provider.logo ? (
                        <img src={getImageUrl(provider.logo)} alt={provider.name} className="provider-hero-logo" />
                    ) : (
                        <h1>{provider.name}</h1>
                    )}
                    <div className="providers-breadcrumb" style={{ marginTop: '10px' }}>
                        <Link to="/">Home</Link> / <Link to="/providers">Providers</Link> / <span>{provider.name}</span>
                    </div>
                </div>
            </div>

            {/* Sticky Control Bar */}
            <div className="provider-control-bar">
                <div className="control-bar-inner">
                    {/* Game Type Slider */}
                    {availableTypes.length > 0 ? (
                        <div className="casino-slider-container">
                            <div className="casino-nav-bar">
                                <button className="nav-scroll-btn left" onClick={() => scroll(navRef, -200)}><FaChevronLeft /></button>
                                <div className="casino-nav-scroll-area" ref={navRef}>
                                    {availableTypes.map((item) => (
                                        <button
                                            key={item.id}
                                            className={`casino-nav-item ${activeType === item.id ? "active" : ""}`}
                                            onClick={() => setActiveType(item.id)}
                                        >
                                            <span className="nav-icon">{getIconForType(item.id)}</span>
                                            <div className="nav-info">
                                                <span className="nav-label">{item.label}</span>
                                                <span className="nav-count" style={{ fontSize: '0.7em', opacity: 0.7 }}>{item.count} games</span>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                                <button className="nav-scroll-btn right" onClick={() => scroll(navRef, 200)}><FaChevronRight /></button>
                            </div>
                        </div>
                    ) : (
                        <div style={{ flex: 1 }}></div>
                    )}

                    {/* Search Box */}
                    <div className="casino-search-container">
                        <div className="casino-search-input-wrapper">
                            <FaSearch className="casino-search-icon" />
                            <input
                                type="text"
                                placeholder={`Search ${provider.name}...`}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="casino-search-input"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Games Grid */}
            <div className="casino-games-container">
                <div className="casino-section-header">
                    <h3>
                        <span style={{ color: '#ff9900' }}>{provider.name}</span>
                        <span style={{ fontSize: '0.8em', color: '#666', marginLeft: '10px' }}>({filteredGames.length} Games)</span>
                    </h3>
                </div>

                {filteredGames.length > 0 ? (
                    <div className="casino-games-grid">
                        {filteredGames.map((game) => (
                            <GameCard key={game._id} game={game} />
                        ))}
                    </div>
                ) : (
                    <div className="no-games-found"><p>No games found.</p></div>
                )}
            </div>

            <LoginModal show={showLogin} handleClose={() => setShowLogin(false)} openSignup={() => setShowLogin(false)} />
        </div>
    );
};

export default ProviderSinglePage;
